import React from 'react';
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Divider, IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Skeleton, Tooltip,
    Typography
} from '@mui/material';
import {ContentCopy as CopyIcon} from '@mui/icons-material';
import {Character, Reports} from '../../types/simc';
import {DiscordHeader} from "./discordHeader";

interface CharacterSidebarProps {
    characterGroups: Reports[],
    onSelectCharacter: (character: string) => void;
    selectedCharacter?: string;
    isLoading: boolean;
}

export const CharacterSidebar: React.FC<CharacterSidebarProps> = ({
                                                                      isLoading,
                                                                      characterGroups,
                                                                      onSelectCharacter,
                                                                      selectedCharacter}) => {



    const copyLatest = async (character: Character) => {
        try {
            let report = character.reports[0];
            await navigator.clipboard.writeText(report.report);
            // You could add a toast notification here
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    }

    return (
        <Paper
            elevation={2}
            sx={{
                height: '100%',
                overflow: 'auto',
                borderRadius: 2
            }}
        >

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Characters
                    </Typography>
                    {isLoading && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                </Box>

                {isLoading ? (
                    // Loading skeleton
                    <Box>
                        {[1, 2, 3].map((i) => (
                            <Box key={i} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                                    <Skeleton variant="text" width={120} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : characterGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                        No reports uploaded yet.
                        Paste SimC data to get started!
                    </Typography>
                ) : (
                    characterGroups.map(group => (
                        <Box key={group.discord_id} sx={{ mb: 3 }}>
                            <DiscordHeader id={group.discord_id}/>
                            {/* Character List (grouped by character name) */}
                            <List dense>
                                {/* Group reports by character name */}
                                {group.characters.sort((a, b) => a.name.localeCompare(b.name))
                                    .map((character) => {
                                        const isSelected = character.name === selectedCharacter;
                                        return (
                                            <Paper
                                                key={`${character.name}`}
                                                sx={{
                                                    p: 1,
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    mb: 0.5,
                                                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                }}
                                                onClick={() => onSelectCharacter(character.name)}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'stretch'
                                                }}>
                                                    <Typography variant="h6">{character.name}</Typography>
                                                    <Tooltip title="Copy to clipboard">
                                                        <IconButton size="small" onClick={() => copyLatest(character)}>
                                                            <CopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mt: 0.5
                                                }}>
                                                    <Chip
                                                        label={`${character.reports.length} report${character.reports.length > 1 ? 's' : ''}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Latest: {new Date(character.reports[0].uploadedAt!).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Paper>)
                                    })
                                }
                            </List>

                            {characterGroups.indexOf(group) < characterGroups.length - 1 && (
                                <Divider sx={{ mt: 2 }} />
                            )}
                        </Box>
                    ))
                )}
            </Box>
        </Paper>
    );
};
